import type { Breakpoint } from "@mui/material/styles"
import type { NavSectionProps } from "src/common/nav-section"

import { toast } from "sonner"
import { useRef, useState } from "react"
import { useAuthStore } from "@/src/store"
import useIsMobile from "@/src/hooks/useIsMobile"
import { uploadWorkspaceLogo } from "@/src/services/company"
import OnlyroundLogo from "@/src/assets/icons/ic_onlyround.svg?react"
import OnlyroundLogoLong from "@/src/assets/icons/ic_onlyround_long.svg?react"

import Box from "@mui/material/Box"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"

import { varAlpha } from "src/theme/styles"
import { Scrollbar } from "src/common/scrollbar"
import { NavSectionVertical } from "src/common/nav-section"

// ----------------------------------------------------------------------

export type NavVerticalProps = NavSectionProps & {
    layoutQuery: Breakpoint
    slots?: {
        bottomArea?: React.ReactNode | ((props: { isExpanded: boolean }) => React.ReactNode)
    }
}

export function NavVertical({ sx, data, slots, layoutQuery, ...other }: NavVerticalProps) {
    const theme = useTheme()
    const [isHovering, setIsHovering] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const user = useAuthStore((state) => state.user)
    const setUser = useAuthStore((state) => state.setUser)

    // Responsive behavior: use existing useIsMobile hook
    const isMobile = useIsMobile()
    const isDesktop = !isMobile
    // Detect touch devices to prevent hover expansion
    const isTouchDevice = useMediaQuery("(hover: none)")

    const handleFileUpload = async (file: FileList | null) => {
        if (!file?.[0]) return
        const fileSize = (file[0].size ?? 0) / 1024
        if (fileSize > 1024) {
            toast.error("File size must be less than 1MB")
            return
        }
        setIsUploading(true)
        const resp = await uploadWorkspaceLogo(file[0])
        setIsUploading(false)
        if (resp.status === "success" && resp.data) {
            const allWorkspaces = user?.workspaces
            setUser({
                activeWorkspace: resp.data,
                workspaces:
                    allWorkspaces?.map((workspace) => (workspace.id === resp.data?.id ? resp.data : workspace)) ?? [],
            })
        } else {
            toast.error("Failed to upload logo")
        }
    }

    // Sidebar expands on hover
    const shouldExpand = isHovering && !isTouchDevice
    const transitionDuration = 300
    const transitionEasing = "cubic-bezier(0.4, 0.0, 0.2, 1)"

    return (
        <Box
            onMouseEnter={() => !isTouchDevice && setIsHovering(true)}
            onMouseLeave={() => {
                if (!isTouchDevice) {
                    setIsHovering(false)
                }
            }}
            sx={{
                top: 0,
                left: 0,
                height: 1,
                display: isDesktop ? "flex" : "none",
                position: "fixed",
                flexDirection: "column",
                bgcolor: "var(--layout-nav-bg)",
                zIndex: 1101,
                width: shouldExpand ? "var(--layout-nav-vertical-width)" : "var(--layout-nav-mini-width)",
                borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey["500Channel"], 0.12)})`,
                boxShadow: shouldExpand
                    ? `4px 0 24px ${varAlpha(theme.vars.palette.grey["500Channel"], 0.16)}`
                    : "none",
                backdropFilter: shouldExpand ? "blur(8px)" : "none",
                willChange: "width, transform",
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                transition: theme.transitions.create(["width", "box-shadow", "backdrop-filter"], {
                    duration: `${transitionDuration}ms`,
                    easing: transitionEasing,
                }),
                // Enhanced text label transitions with staggered timing
                "& .nav-item-text": {
                    opacity: shouldExpand ? 1 : 0,
                    width: shouldExpand ? "auto" : 0,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    transition: theme.transitions.create(["opacity", "width"], {
                        duration: `${transitionDuration}ms`,
                        easing: transitionEasing,
                        delay: shouldExpand ? "150ms" : "0ms",
                    }),
                },
                ...sx,
            }}
        >
            {/* Logo Section with Crossfade */}
            <Box
                sx={{ py: 2, overflow: "hidden", position: "relative", minHeight: "56px", px: shouldExpand ? 2 : 0.5 }}
            >
                <Box
                    onClick={() => {
                        window.location.href = "/"
                    }}
                    sx={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        position: "relative",
                        minHeight: "40px",
                        paddingLeft: shouldExpand ? "8px" : "18px",
                        transition: theme.transitions.create(["padding-left"], {
                            duration: `${transitionDuration}ms`,
                            easing: transitionEasing,
                        }),
                    }}
                >
                    {/* Short logo (visible when collapsed) */}
                    <Box
                        sx={{
                            position: shouldExpand ? "absolute" : "relative",
                            opacity: shouldExpand ? 0 : 1,
                            transform: shouldExpand ? "scale(0.9)" : "scale(1)",
                            transition: theme.transitions.create(["opacity", "transform"], {
                                duration: `${transitionDuration}ms`,
                                easing: transitionEasing,
                            }),
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                        }}
                    >
                        <OnlyroundLogo style={{ width: "32px", height: "32px" }} />
                    </Box>
                    {/* Long logo (visible when expanded) */}
                    <Box
                        sx={{
                            position: shouldExpand ? "relative" : "absolute",
                            opacity: shouldExpand ? 1 : 0,
                            transform: shouldExpand ? "scale(1)" : "scale(0.9)",
                            transition: theme.transitions.create(["opacity", "transform"], {
                                duration: `${transitionDuration}ms`,
                                easing: transitionEasing,
                            }),
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                        }}
                    >
                        <OnlyroundLogoLong style={{ maxWidth: "160px", height: "auto" }} />
                    </Box>
                </Box>
            </Box>

            {/* Navigation Items */}
            <Scrollbar fillContent sx={{ flex: 1 }}>
                <NavSectionVertical
                    data={data}
                    sx={{
                        px: shouldExpand ? 2 : 0.5,
                        flex: "1 1 auto",
                        transition: theme.transitions.create(["padding"], {
                            duration: `${transitionDuration}ms`,
                            easing: transitionEasing,
                        }),
                    }}
                    {...other}
                />
            </Scrollbar>

            {/* Bottom Section */}
            {slots?.bottomArea && (
                <Box
                    sx={{
                        p: shouldExpand ? 2 : 1,
                        transition: theme.transitions.create(["padding"], {
                            duration: `${transitionDuration}ms`,
                            easing: transitionEasing,
                        }),
                    }}
                >
                    {typeof slots.bottomArea === "function"
                        ? slots.bottomArea({ isExpanded: shouldExpand })
                        : slots.bottomArea}
                </Box>
            )}

            {/* Hidden file input for logo upload */}
            <input
                ref={inputRef}
                type="file"
                style={{ display: "none" }}
                onChange={(event) => handleFileUpload(event.target.files)}
                accept=".svg,.jpg,.jpeg,.png"
            />
        </Box>
    )
}
